import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, notification, Popconfirm, Radio, Spin, Switch } from "antd";
import { ArrowLeft, EyeOff, Loader2, RotateCcw, Trash2 } from "lucide-react";
import usePageTitle from "../../core/hooks/UsePageTitle";
import useMaterias from "../../core/hooks/UseMaterias";
import useBancas from "../../core/hooks/UseBanca";
import DefaultLayout from "../layouts/default/Default.layout";
import AccessDenied from "../components/AccessDenied/AccessDenied";
import CertameService from "../../sdk/services/CertameService";
import { readApiError } from "../../sdk/errors/apiError";
import { Certame } from "../../sdk/@types";
import { CertameEditorShell } from "../components/CertameEditorShell/CertameEditorShell";

const numberFormatter = new Intl.NumberFormat("pt-BR");

const questionsLabel = (n: number) =>
  n === 1 ? "1 questão" : `${numberFormatter.format(n)} questões`;

// Mesmo corte usado em `CertameEditar.view.tsx` para ids longos.
function abbreviateId(id: string): string {
  if (id.length <= 12) return id;
  return `${id.slice(0, 6)}…${id.slice(-4)}`;
}

// Mesma chave de `CertameEditar.view.tsx` — uma tag só existe dentro de uma
// matéria, então o par (subjectId, tag) identifica a entrada.
const tagChangeKey = (subjectId: string, tag: string) => `${subjectId}:::${tag}`;

interface PendingTagChange {
  subjectId: string;
  tag: string;
  previousVisible: boolean;
}

/**
 * `/certame/:id/tags` — revisão de tags duplicadas e trechos de comentário,
 * na mesma casca de duas colunas de `CertameEditar.view.tsx` (`design.md`,
 * "A shared CertameEditorShell component"): coluna esquerda idêntica
 * (cabeçalho do certame, "Dados do certame", "Matérias vinculadas",
 * "Bancas", barra de alterações não salvas), coluna direita com o conteúdo
 * desta tela.
 *
 * A coluna esquerda é somente leitura aqui — esta tela não tem campo de
 * formulário próprio para ficar "sujo" (`design.md`, Decisão 1); a única
 * fonte de alterações não salvas é a visibilidade de tag que "Ocultar"
 * muda a partir de um trecho de comentário, rastreada no mesmo formato
 * `pendingTagChanges` que o editor usa, para alimentar a mesma barra.
 *
 * Esta view só renderiza grupos que a API já classificou
 * (`getDuplicateTagGroups`): a heurística de similaridade vive no back
 * (design.md, "Duplicate-tag grouping… live in design, enforced by back") —
 * nada aqui recalcula semelhança entre tags.
 *
 * A segunda seção, "Trechos de comentário salvos como tag", lista o que
 * `getCommentSnippetTags` já classificou como provável trecho de comentário
 * (heurística também no back). "Excluir" chama `deleteTag`, removendo o valor
 * de toda questão que o usa no certame inteiro; "Ocultar" chama
 * `setTagVisibility` em vez disso — o valor continua como tag das questões,
 * só sai do filtro de Assunto do aluno. Como a visibilidade é uma chave
 * (subjectId, tag) (design.md, "Tag visibility is scoped per subject") e
 * `getCommentSnippetTags` só devolve `subjectName`, o `subjectId` é resolvido
 * aqui via `useMaterias()` (`/subjects` — não a rota escopada por assinante,
 * admin não tem assinante, mesma armadilha do R12/R13).
 */
export default function CertameRevisarTagsView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  usePageTitle("Revisar tags");

  const { materias, fetchMaterias } = useMaterias();
  const { bancas, fetchBancas } = useBancas();

  // ───────────────────────── Coluna esquerda (somente leitura) ─────────────────────────
  const [certame, setCertame] = useState<Certame.Certame | null>(null);
  const [certameLoading, setCertameLoading] = useState(true);
  const [questionCount, setQuestionCount] = useState<number | null>(null);

  const [tagPanel, setTagPanel] = useState<Certame.TagPanel | null>(null);

  // Alterações de visibilidade feitas nesta tela (via "Ocultar" num trecho de
  // comentário), ainda não confirmadas — mesmo formato que
  // `CertameEditar.view.tsx` usa para a barra de alterações não salvas.
  const [pendingTagChanges, setPendingTagChanges] = useState<
    Map<string, PendingTagChange>
  >(new Map());
  const [cancelling, setCancelling] = useState(false);
  const originalVisibilityRef = useRef<Map<string, boolean>>(new Map());

  // ───────────────────────── Coluna direita (conteúdo desta tela) ─────────────────────────
  const [groups, setGroups] = useState<Certame.DuplicateTagGroup[]>([]);
  const [targets, setTargets] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ReturnType<typeof readApiError> | null>(null);
  const [busyGroupId, setBusyGroupId] = useState<string | null>(null);
  const [bulkMergingGroups, setBulkMergingGroups] = useState(false);

  const [snippets, setSnippets] = useState<Certame.CommentSnippetTag[]>([]);
  const [snippetsLoading, setSnippetsLoading] = useState(true);
  const [snippetsError, setSnippetsError] = useState<ReturnType<
    typeof readApiError
  > | null>(null);
  const [busySnippetKey, setBusySnippetKey] = useState<string | null>(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    fetchMaterias();
    fetchBancas();
  }, [fetchMaterias, fetchBancas]);

  // Certame + contagem de questões — mesmas chamadas que `CertameEditar.view.tsx`
  // faz para a mesma coluna esquerda.
  useEffect(() => {
    if (!id) return;

    let cancelled = false;
    setCertameLoading(true);

    CertameService.getById(id)
      .then((row) => {
        if (!cancelled) setCertame(row);
      })
      .catch((err) => {
        if (cancelled) return;
        notification.error({
          message: "Erro ao carregar certame",
          description: err instanceof Error ? err.message : "Erro inesperado",
        });
        navigate("/certame");
      })
      .finally(() => {
        if (!cancelled) setCertameLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  useEffect(() => {
    if (!id) return;

    let cancelled = false;

    CertameService.getQuestionCount(id)
      .then((res) => {
        if (!cancelled) setQuestionCount(res.total ?? 0);
      })
      .catch(() => {
        if (!cancelled) setQuestionCount(null);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  // Painel de tags — mesma chamada que o editor usa, aqui só para a contagem
  // de tags por matéria em "Matérias vinculadas" e para a fotografia de
  // visibilidade original contra a qual "Ocultar" calcula o pendente.
  useEffect(() => {
    if (!id) return;

    let cancelled = false;

    CertameService.getTagPanel(id)
      .then((panel) => {
        if (cancelled) return;
        setTagPanel(panel);

        const snapshot = new Map<string, boolean>();
        panel.bySubject.forEach((subject) =>
          subject.tags.forEach((tag) => {
            snapshot.set(tagChangeKey(subject.subjectId, tag.value), tag.visible);
          }),
        );
        originalVisibilityRef.current = snapshot;
        setPendingTagChanges(new Map());
      })
      .catch(() => {
        if (!cancelled) setTagPanel(null);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  const materiasById = useMemo(() => {
    const map = new Map<string, { id?: string; name?: string }>();
    (materias ?? []).forEach((m) => {
      if (m.id) map.set(m.id, m);
    });
    return map;
  }, [materias]);

  const tagCountBySubject = useMemo(() => {
    const map = new Map<string, number>();
    (tagPanel?.bySubject ?? []).forEach((s) => {
      if (s.subjectId) map.set(s.subjectId, s.totalTags ?? 0);
    });
    return map;
  }, [tagPanel]);

  const subjectIds = certame?.subjectIds ?? [];

  const bancaNames = useMemo(() => {
    const ids = certame?.testingAgencyIds ?? [];
    if (ids.length === 0) return [];
    const byId = new Map(
      (bancas ?? []).map((b: any) => [b.id, `${b.year} - ${b.name}`]),
    );
    return ids.map((bid) => byId.get(bid) ?? bid);
  }, [certame, bancas]);

  /** `getCommentSnippetTags` só devolve `subjectName`, mas `setTagVisibility`
   * é escopado por `subjectId` (design.md, "Tag visibility is scoped per
   * subject") — resolve pelo catálogo completo de matérias, não pela rota
   * escopada por assinante (admin não tem assinante). */
  const subjectIdByName = useMemo(() => {
    const map = new Map<string, string>();
    (materias ?? []).forEach((m) => {
      if (m.id && m.name) map.set(m.name, m.id);
    });
    return map;
  }, [materias]);

  const updatePendingTagChanges = useCallback(
    (subjectId: string, tagValues: string[], nextVisible: boolean) => {
      setPendingTagChanges((prev) => {
        const next = new Map(prev);
        tagValues.forEach((tagValue) => {
          const key = tagChangeKey(subjectId, tagValue);
          const original = originalVisibilityRef.current.get(key);
          if (original === undefined) return;
          if (original === nextVisible) {
            next.delete(key);
          } else {
            next.set(key, { subjectId, tag: tagValue, previousVisible: original });
          }
        });
        return next;
      });
    },
    [],
  );

  const unsavedChangesCount = pendingTagChanges.size;

  // "Cancelar" desfaz, nesta sessão, as ocultações feitas a partir desta
  // tela (cada uma já foi persistida no clique — ver handleHideSnippet),
  // mesmo padrão de `CertameEditar.view.tsx`'s `handleCancel`.
  const handleCancel = useCallback(async () => {
    if (id && pendingTagChanges.size > 0) {
      setCancelling(true);
      await Promise.all(
        Array.from(pendingTagChanges.values()).map(
          ({ subjectId, tag, previousVisible }) =>
            CertameService
              .setTagVisibility(id, {
                subjectId,
                tags: [tag],
                visible: previousVisible,
              })
              .catch((err) => console.error(err)),
        ),
      );
      setCancelling(false);
    }
    navigate(id ? `/certame/${id}` : "/certame");
  }, [id, pendingTagChanges, navigate]);

  // Não há dado de formulário para enviar aqui — as ocultações já foram
  // persistidas no clique. "Salvar alterações" apenas confirma e volta ao
  // editor do certame.
  const handleSave = useCallback(() => {
    navigate(id ? `/certame/${id}` : "/certame");
  }, [id, navigate]);

  const load = useCallback(() => {
    if (!id) return () => {};

    let alive = true;
    setLoading(true);
    setError(null);

    CertameService.getDuplicateTagGroups(id)
      .then((data) => {
        if (!alive) return;
        const list = data?.groups ?? [];
        setGroups(list);
        setTargets(
          Object.fromEntries(list.map((g) => [g.groupId, g.suggestedTarget])),
        );
      })
      .catch((err) => {
        if (!alive) return;
        setError(readApiError(err));
        setGroups([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [id]);

  useEffect(() => load(), [load]);

  const loadSnippets = useCallback(() => {
    if (!id) return () => {};

    let alive = true;
    setSnippetsLoading(true);
    setSnippetsError(null);

    CertameService.getCommentSnippetTags(id)
      .then((data) => {
        if (!alive) return;
        setSnippets(data?.items ?? []);
      })
      .catch((err) => {
        if (!alive) return;
        setSnippetsError(readApiError(err));
        setSnippets([]);
      })
      .finally(() => {
        if (alive) setSnippetsLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [id]);

  useEffect(() => loadSnippets(), [loadSnippets]);

  const removeGroup = useCallback((groupId: string) => {
    setGroups((prev) => prev.filter((g) => g.groupId !== groupId));
  }, []);

  const removeSnippetsByValue = useCallback((value: string) => {
    setSnippets((prev) => prev.filter((s) => s.value !== value));
  }, []);

  const removeSnippet = useCallback((value: string, subjectName: string) => {
    setSnippets((prev) =>
      prev.filter((s) => !(s.value === value && s.subjectName === subjectName)),
    );
  }, []);

  /**
   * 404/409 = outro admin já tratou este trecho — mesmo padrão informativo
   * de `handleMerge`/`handleIgnore` abaixo.
   */
  const handleDeleteSnippet = useCallback(
    async (item: Certame.CommentSnippetTag) => {
      if (!id || busySnippetKey) return;
      const key = `delete:${item.value}`;

      setBusySnippetKey(key);
      try {
        await CertameService.deleteTag(id, item.value);
        removeSnippetsByValue(item.value);
        notification.success({
          message: "Trecho excluído",
          description:
            "O valor foi removido da lista de tags de todas as questões que o usavam.",
        });
      } catch (err) {
        const { status } = readApiError(err);
        if (status === 404 || status === 409) {
          removeSnippetsByValue(item.value);
          notification.info({
            message: "Já removido",
            description: "Outro administrador já excluiu este trecho.",
          });
        } else {
          notification.error({
            message: "Erro ao excluir",
            description: "Não foi possível excluir o trecho. Tente novamente.",
          });
        }
      } finally {
        setBusySnippetKey(null);
      }
    },
    [id, busySnippetKey, removeSnippetsByValue],
  );

  const handleHideSnippet = useCallback(
    async (item: Certame.CommentSnippetTag) => {
      if (!id || busySnippetKey) return;
      const subjectId = subjectIdByName.get(item.subjectName);
      if (!subjectId) {
        notification.error({
          message: "Matéria não encontrada",
          description: `Não foi possível identificar a matéria "${item.subjectName}" para ocultar este trecho.`,
        });
        return;
      }
      const key = `hide:${item.value}:${item.subjectName}`;

      setBusySnippetKey(key);
      try {
        await CertameService.setTagVisibility(id, {
          subjectId,
          tags: [item.value],
          visible: false,
        });
        removeSnippet(item.value, item.subjectName);
        updatePendingTagChanges(subjectId, [item.value], false);
        notification.success({
          message: "Trecho ocultado",
          description:
            "O valor continua como tag das questões, mas não aparece mais no filtro de Assunto do aluno.",
        });
      } catch (err) {
        const { status } = readApiError(err);
        if (status === 404 || status === 409) {
          removeSnippet(item.value, item.subjectName);
          notification.info({
            message: "Já tratado",
            description: "Outro administrador já tratou este trecho.",
          });
        } else {
          notification.error({
            message: "Erro ao ocultar",
            description: "Não foi possível ocultar o trecho. Tente novamente.",
          });
        }
      } finally {
        setBusySnippetKey(null);
      }
    },
    [id, busySnippetKey, subjectIdByName, removeSnippet, updatePendingTagChanges],
  );

  const handleDeleteAllSnippets = useCallback(async () => {
    if (!id || bulkDeleting) return;

    setBulkDeleting(true);
    try {
      await CertameService.deleteAllCommentSnippets(id);
      setSnippets([]);
      notification.success({
        message: "Trechos excluídos",
        description:
          "Todos os trechos de comentário foram removidos das questões correspondentes.",
      });
    } catch (err) {
      notification.error({
        message: "Erro ao excluir todas",
        description: "Não foi possível excluir os trechos. Tente novamente.",
      });
    } finally {
      setBulkDeleting(false);
    }
  }, [id, bulkDeleting]);

  /**
   * 404/409 significam "outro admin já mesclou/ignorou este grupo nos
   * últimos segundos" — aviso informativo, não erro, mesmo padrão de
   * `ReportsQueue.tsx` ("Outro admin já mesclou o mesmo grupo").
   */
  const handleMerge = useCallback(
    async (group: Certame.DuplicateTagGroup) => {
      if (!id || busyGroupId) return;
      const target = targets[group.groupId] ?? group.suggestedTarget;

      setBusyGroupId(group.groupId);
      try {
        await CertameService.mergeTagGroup(id, { groupId: group.groupId, target });
        removeGroup(group.groupId);
        notification.success({
          message: "Tags mescladas",
          description: `As variantes do grupo foram unificadas em "${target}".`,
        });
      } catch (err) {
        const { status } = readApiError(err);
        if (status === 404 || status === 409) {
          removeGroup(group.groupId);
          notification.info({
            message: "Grupo já resolvido",
            description: "Outro administrador já tratou este grupo de tags.",
          });
        } else {
          notification.error({
            message: "Erro ao mesclar",
            description: "Não foi possível mesclar o grupo. Tente novamente.",
          });
        }
      } finally {
        setBusyGroupId(null);
      }
    },
    [id, busyGroupId, targets, removeGroup],
  );

  const handleIgnore = useCallback(
    async (group: Certame.DuplicateTagGroup) => {
      if (!id || busyGroupId) return;

      setBusyGroupId(group.groupId);
      try {
        await CertameService.ignoreTagGroup(id, { groupId: group.groupId });
        removeGroup(group.groupId);
      } catch (err) {
        const { status } = readApiError(err);
        if (status === 404 || status === 409) {
          removeGroup(group.groupId);
        } else {
          notification.error({
            message: "Erro ao ignorar",
            description: "Não foi possível ignorar o grupo. Tente novamente.",
          });
        }
      } finally {
        setBusyGroupId(null);
      }
    },
    [id, busyGroupId, removeGroup],
  );

  const totalGroupQuestions = useMemo(
    () => groups.reduce((sum, group) => sum + group.totalQuestions, 0),
    [groups],
  );

  /**
   * "Mesclar sugestões (N)" — aceita de uma vez o alvo sugerido de todo grupo
   * listado (design.md, Decisão 1). O back recalcula cada grupo no momento da
   * mesclagem, então um grupo já resolvido por outro admin entra em
   * `skipped`, não em erro — a resposta ainda é 200 e os demais grupos foram
   * mesclados normalmente.
   */
  const handleMergeAll = useCallback(async () => {
    if (!id || groups.length === 0 || bulkMergingGroups) return;

    setBulkMergingGroups(true);
    try {
      const result = await CertameService.mergeAllTagGroups(id);
      const totalMerged = result?.totalMerged ?? 0;
      const totalSkipped = result?.totalSkipped ?? 0;

      notification.success({
        message: "Tags mescladas",
        description:
          totalSkipped > 0
            ? `${
                totalMerged === 1
                  ? "1 grupo foi mesclado"
                  : `${numberFormatter.format(totalMerged)} grupos foram mesclados`
              } em seus valores sugeridos. ${
                totalSkipped === 1
                  ? "1 grupo já tinha sido resolvido"
                  : `${numberFormatter.format(totalSkipped)} grupos já tinham sido resolvidos`
              } por outro administrador e foi ignorado.`
            : `${
                totalMerged === 1
                  ? "1 grupo foi mesclado"
                  : `${numberFormatter.format(totalMerged)} grupos foram mesclados`
              } em seus valores sugeridos.`,
      });
      load();
    } catch (err) {
      notification.error({
        message: "Erro ao mesclar sugestões",
        description: "Não foi possível mesclar os grupos. Tente novamente.",
      });
    } finally {
      setBulkMergingGroups(false);
    }
  }, [id, groups.length, bulkMergingGroups, load]);

  const failed = useMemo(() => Boolean(error && !error.kind), [error]);

  // ───────────────────────── Coluna esquerda — JSX ─────────────────────────

  const header = (
    <div className="justify-start mb-2 flex w-full items-center gap-3 border-b border-edge p-2">
      <Button
        type="text"
        icon={<ArrowLeft size={18} />}
        onClick={() => navigate(id ? `/certame/${id}` : "/certame")}
        aria-label="Voltar para o editor do certame"
      />
      <div>
        <p className="text-2xl font-bold text-ink">Revisar tags</p>
        <p className="text-base font-normal text-ink-secondary">
          {certame?.abbreviation
            ? `${certame.abbreviation} — tags possivelmente duplicadas e trechos de comentário salvos como tag`
            : "Tags possivelmente duplicadas e trechos de comentário salvos como tag"}
        </p>
      </div>
    </div>
  );

  const dadosDoCertame = (
    <div>
      <p className="mb-2 text-lg font-semibold text-ink">Dados do certame</p>
      <div className="flex flex-col gap-2">
        <div>
          <p className="mb-1 text-sm text-ink-secondary">Sigla</p>
          <div className="flex h-[40px] w-full items-center rounded-input border border-edge bg-surface-muted px-3 py-2 text-ink">
            {certame?.abbreviation || "—"}
          </div>
        </div>
        <div>
          <p className="mb-1 text-sm text-ink-secondary">Descrição</p>
          <div
            className="flex h-[40px] w-full items-center truncate rounded-input border border-edge bg-surface-muted px-3 py-2 text-ink"
            title={certame?.description}
          >
            {certame?.description || "—"}
          </div>
        </div>
        <div>
          <p className="mb-1 text-sm text-ink-secondary">Status</p>
          <Switch
            checked={!certame?.disabled}
            disabled
            checkedChildren="Ativo"
            unCheckedChildren="Desativado"
          />
        </div>
      </div>
    </div>
  );

  const materiasVinculadas = (
    <div>
      <p className="mb-1 text-lg font-semibold text-ink">Matérias vinculadas</p>
      <p className="mb-3 text-sm text-ink-secondary">
        Questões e tags de cada matéria vinculada ao certame.
      </p>

      <div className="flex flex-col gap-2">
        {subjectIds.length === 0 && (
          <p className="text-sm text-ink-muted">Nenhuma matéria vinculada.</p>
        )}

        {subjectIds.map((subjectId) => {
          const materia = materiasById.get(subjectId);

          if (!materia) {
            return (
              <div
                key={subjectId}
                className="rounded-card border border-warn/40 bg-warn/[0.08] px-4 py-3"
              >
                <p className="text-sm font-semibold text-warn-ink">
                  Matéria não encontrada
                </p>
                <p className="text-xs text-ink-muted font-mono">
                  {abbreviateId(subjectId)}
                </p>
              </div>
            );
          }

          const tagCount = tagCountBySubject.get(subjectId) ?? 0;

          return (
            <div
              key={subjectId}
              className="rounded-card border border-edge bg-surface px-4 py-3"
            >
              <p className="text-sm font-semibold text-ink">{materia.name}</p>
              <p className="text-xs text-ink-secondary">
                {questionCount == null
                  ? "— questões"
                  : `${numberFormatter.format(questionCount)} questões`}
                {" · "}
                {`${numberFormatter.format(tagCount)} tags`}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );

  const bancasSection = (
    <div>
      <p className="mb-1 text-lg font-semibold text-ink">Banca(s) do certame</p>
      <p className="mb-3 text-sm text-ink-secondary">
        Vazio significa sem restrição: o aluno vê todas as bancas que tenham
        questão nas matérias deste certame.
      </p>
      {bancaNames.length === 0 ? (
        <p className="text-sm text-ink-muted">Nenhuma banca — sem restrição.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {bancaNames.map((name) => (
            <span
              key={name}
              className="rounded-chip border border-edge bg-surface-muted px-2.5 py-1 text-xs text-ink"
            >
              {name}
            </span>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <DefaultLayout hideFilter>
      <div>
        {certameLoading ? (
          <div className="flex items-center justify-center py-24">
            <Spin size="large" />
          </div>
        ) : (
          <CertameEditorShell
            header={header}
            unsavedBar={{
              count: unsavedChangesCount,
              onCancel: handleCancel,
              onSave: handleSave,
              cancelDisabled: cancelling,
            }}
            dadosDoCertame={dadosDoCertame}
            materiasVinculadas={materiasVinculadas}
            bancas={bancasSection}
          >
            {error?.kind ? (
              <AccessDenied
                kind={error.kind}
                detail={error.raw}
                variant="panel"
                onNavigate={navigate}
              />
            ) : (
              <div className="flex flex-col gap-5">
                <section className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-ink">
                      Possíveis duplicadas
                    </h2>
                    <div className="flex items-center gap-2">
                      <Popconfirm
                        title="Mesclar todas as sugestões"
                        description={`${
                          groups.length === 1
                            ? "1 grupo"
                            : `${numberFormatter.format(groups.length)} grupos`
                        } serão mesclados em seus valores sugeridos, afetando ${questionsLabel(
                          totalGroupQuestions,
                        )} no total.`}
                        okText="Mesclar todas"
                        cancelText="Cancelar"
                        onConfirm={handleMergeAll}
                        disabled={groups.length === 0}
                      >
                        <Button
                          type="primary"
                          loading={bulkMergingGroups}
                          disabled={groups.length === 0}
                        >
                          {`Mesclar sugestões (${numberFormatter.format(groups.length)})`}
                        </Button>
                      </Popconfirm>
                      <Button
                        type="text"
                        icon={<RotateCcw size={15} />}
                        onClick={load}
                        aria-label="Atualizar grupos"
                      >
                        Atualizar
                      </Button>
                    </div>
                  </div>

                  {loading && (
                    <div
                      role="status"
                      aria-label="Carregando grupos de tags duplicadas"
                      className="flex items-center justify-center rounded-card border border-edge bg-surface py-10"
                    >
                      <Loader2 size={18} className="animate-spin text-ink-muted" />
                    </div>
                  )}

                  {!loading && failed && (
                    <div className="rounded-card border border-edge bg-surface p-4 text-sm">
                      <p className="text-ink">
                        Não foi possível carregar os grupos de tags.
                      </p>
                      {error?.raw && (
                        <p className="mt-1 text-xs text-ink-muted">{error.raw}</p>
                      )}
                      <Button className="mt-3" icon={<RotateCcw size={15} />} onClick={load}>
                        Tentar de novo
                      </Button>
                    </div>
                  )}

                  {!loading && !failed && groups.length === 0 && (
                    <p className="rounded-card border border-edge bg-surface p-4 text-sm text-ink-muted">
                      Nenhum grupo de tags possivelmente duplicadas para revisar.
                    </p>
                  )}

                  {!loading &&
                    !failed &&
                    groups.map((group) => {
                      const target = targets[group.groupId] ?? group.suggestedTarget;
                      const busy = busyGroupId === group.groupId;

                      return (
                        <div
                          key={group.groupId}
                          data-testid="duplicate-tag-group"
                          className="flex flex-col gap-3 rounded-card border border-edge bg-surface p-4"
                        >
                          <div className="flex flex-wrap items-baseline justify-between gap-2">
                            <p className="text-sm font-semibold text-ink">
                              Mesclar em &quot;{target}&quot;
                            </p>
                            <p className="text-xs text-ink-muted">
                              {questionsLabel(group.totalQuestions)} afetadas no total
                            </p>
                          </div>

                          <Radio.Group
                            value={target}
                            onChange={(e) =>
                              setTargets((prev) => ({
                                ...prev,
                                [group.groupId]: e.target.value,
                              }))
                            }
                            className="flex flex-col gap-1.5"
                          >
                            {group.variants.map((variant) => (
                              <Radio key={variant.value} value={variant.value} className="text-sm">
                                <span className="text-ink">{variant.value}</span>{" "}
                                <span className="text-xs text-ink-muted">
                                  ({questionsLabel(variant.count)})
                                </span>
                                {variant.value === group.suggestedTarget && (
                                  <span className="ml-1.5 rounded-chip border border-select/[0.42] bg-select/[0.14] px-1.5 py-0.5 text-[11px] font-medium text-select-ink">
                                    sugerido
                                  </span>
                                )}
                              </Radio>
                            ))}
                          </Radio.Group>

                          <div className="flex items-center gap-2">
                            <Popconfirm
                              title="Mesclar grupo de tags"
                              description={`As variantes serão unificadas em "${target}" em toda questão que as usa, mesmo em outro certame.`}
                              okText="Mesclar"
                              cancelText="Cancelar"
                              onConfirm={() => handleMerge(group)}
                            >
                              <Button type="primary" loading={busy}>
                                Mesclar
                              </Button>
                            </Popconfirm>
                            <Button onClick={() => handleIgnore(group)} disabled={busy}>
                              Ignorar
                            </Button>
                            {busy && <Spin size="small" />}
                          </div>
                        </div>
                      );
                    })}
                </section>

                <section className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-ink">
                      Trechos de comentário salvos como tag
                    </h2>
                    <div className="flex items-center gap-2">
                      <Button
                        type="text"
                        icon={<RotateCcw size={15} />}
                        onClick={loadSnippets}
                        aria-label="Atualizar trechos de comentário"
                      >
                        Atualizar
                      </Button>
                      <Popconfirm
                        title="Excluir todos os trechos"
                        description={`Os ${numberFormatter.format(snippets.length)} valores listados serão removidos das tags de todas as questões correspondentes.`}
                        okText="Excluir todas"
                        okButtonProps={{ danger: true }}
                        cancelText="Cancelar"
                        onConfirm={handleDeleteAllSnippets}
                        disabled={snippets.length === 0}
                      >
                        <Button
                          danger
                          icon={<Trash2 size={15} />}
                          loading={bulkDeleting}
                          disabled={snippets.length === 0}
                        >
                          {`Excluir todas (${numberFormatter.format(snippets.length)})`}
                        </Button>
                      </Popconfirm>
                    </div>
                  </div>

                  {snippetsLoading && (
                    <div
                      role="status"
                      aria-label="Carregando trechos de comentário"
                      className="flex items-center justify-center rounded-card border border-edge bg-surface py-10"
                    >
                      <Loader2 size={18} className="animate-spin text-ink-muted" />
                    </div>
                  )}

                  {!snippetsLoading && snippetsError && (
                    <div className="rounded-card border border-edge bg-surface p-4 text-sm">
                      <p className="text-ink">
                        Não foi possível carregar os trechos de comentário.
                      </p>
                      {snippetsError.raw && (
                        <p className="mt-1 text-xs text-ink-muted">{snippetsError.raw}</p>
                      )}
                      <Button
                        className="mt-3"
                        icon={<RotateCcw size={15} />}
                        onClick={loadSnippets}
                      >
                        Tentar de novo
                      </Button>
                    </div>
                  )}

                  {!snippetsLoading &&
                    !snippetsError &&
                    snippets.length === 0 && (
                      <p className="rounded-card border border-edge bg-surface p-4 text-sm text-ink-muted">
                        Nenhum trecho de comentário identificado nas tags deste certame.
                      </p>
                    )}

                  {!snippetsLoading &&
                    !snippetsError &&
                    snippets.map((item) => {
                      const deleteBusy = busySnippetKey === `delete:${item.value}`;
                      const hideBusy =
                        busySnippetKey === `hide:${item.value}:${item.subjectName}`;
                      const busy = deleteBusy || hideBusy;

                      return (
                        <div
                          key={`${item.questionId}:${item.value}`}
                          data-testid="comment-snippet-item"
                          className="flex flex-col gap-2 rounded-card border border-edge bg-surface p-4"
                        >
                          <p className="line-clamp-2 text-sm text-ink" title={item.value}>
                            &quot;{item.value}&quot;
                          </p>
                          <p className="text-xs text-ink-muted">
                            Questão {item.questionRef} · {item.subjectName}
                          </p>

                          <div className="flex items-center gap-2">
                            <Button
                              size="small"
                              icon={<EyeOff size={14} />}
                              onClick={() => handleHideSnippet(item)}
                              loading={hideBusy}
                              disabled={busy && !hideBusy}
                            >
                              Ocultar
                            </Button>
                            <Popconfirm
                              title="Excluir trecho de comentário"
                              description="O valor será removido da lista de tags de toda questão que o usa."
                              okText="Excluir"
                              okButtonProps={{ danger: true }}
                              cancelText="Cancelar"
                              onConfirm={() => handleDeleteSnippet(item)}
                            >
                              <Button
                                size="small"
                                danger
                                icon={<Trash2 size={14} />}
                                loading={deleteBusy}
                                disabled={busy && !deleteBusy}
                              >
                                Excluir
                              </Button>
                            </Popconfirm>
                            {busy && <Spin size="small" />}
                          </div>
                        </div>
                      );
                    })}
                </section>
              </div>
            )}
          </CertameEditorShell>
        )}
      </div>
    </DefaultLayout>
  );
}
